import { useState, useRef } from "react";
import { X, Camera } from "lucide-react";
import ChatHeader from "./ChatHeader";

type ImageItem = { base64: string; mediaType: string; preview: string };

interface PlantSnapModalProps {
  onClose: () => void;
  onAnalyze: (
    apiPrompt: string,
    imageData: { base64: string; mediaType: string },
    displayText: string
  ) => void;
}

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

/** Shown in the chat as the user's bubble — never the injected vision prompt. */
const PLANT_DISPLAY_TEXT = "What can you tell me about this plant? 🪴";

/**
 * Base vision prompt (injected — the user never sees this). If the user typed a
 * question it's spliced in ahead of the health-assessment fallback so Kin answers
 * it first, grounded in what's actually visible.
 */
const buildPlantPrompt = (question: string): string => {
  const trimmed = question.trim();
  const questionLine = trimmed
    ? `The user specifically asked: "${trimmed}". Answer that first, grounded strictly in what is VISIBLE in the photo.`
    : `Assess the plant's health — watering, light, pests, pot/spacing — and give a plain-language care plan.`;

  return `The user has uploaded a photo of a plant. Look at what's actually visible and help them out.

Identify the plant if you can. ${questionLine}
If it's a food plant, include when and how to harvest. Keep it warm and plain-language — no jargon.

End with exactly ONE short follow-up question (for example, whether it's indoors or outdoors, or their growing zone) so you can tighten your advice.

Grounding rule: never state anything you can't actually see in the photo. If you can't tell something from the image (like soil moisture, root health, or the exact variety), say "I can't tell from this photo" rather than guessing.`;
};

const PlantSnapModal = ({ onClose, onAnalyze }: PlantSnapModalProps) => {
  const [image, setImage] = useState<ImageItem | null>(null);
  const [question, setQuestion] = useState("");
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      setError("That image is a little too big. Try a smaller one or a new photo. 💜");
      return;
    }
    setError(null);

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      setImage({
        base64: result.split(",")[1],
        mediaType: file.type || "image/jpeg",
        preview: result,
      });
    };
    reader.onerror = () => setError("Couldn't read that image. Try another one. 💜");
    reader.readAsDataURL(file);
  };

  const handleAnalyze = () => {
    if (!image) return;
    const { base64, mediaType } = image;
    onAnalyze(buildPlantPrompt(question), { base64, mediaType }, PLANT_DISPLAY_TEXT);
  };

  return (
    <div className="fixed inset-0 z-[60] flex flex-col" style={{ backgroundColor: "#F0F0FF" }}>
      {/* Header */}
      <ChatHeader onLogoClick={onClose} />

      <div
        className="flex-1 overflow-y-auto px-4 pb-6 max-w-lg mx-auto w-full relative"
        style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 84px)" }}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute right-2 flex items-center justify-center"
          style={{
            top: "calc(env(safe-area-inset-top, 0px) + 92px)",
            width: 44,
            height: 44,
          }}
        >
          <X size={24} style={{ color: "#54525C" }} />
        </button>
        <h2 className="font-display" style={{ fontSize: 22, color: "#1C1A3E" }}>Snap your plant 🪴</h2>
        <p className="font-body mb-3" style={{ fontSize: 13, color: "#5050C8", paddingRight: 48 }}>
          Show me any plant and ask me anything. Why is it dying, when do I harvest, what even is
          this. I'll take a look.
        </p>

        <div className="mb-3" />

        {!image && (
          <>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full flex flex-col items-center justify-center gap-3 py-12"
              style={{
                border: "2px dashed #C8C8F4",
                borderRadius: 16,
                backgroundColor: "#F8F8FF",
              }}
            >
              <Camera size={32} style={{ color: "#5050C8" }} />
              <span className="font-body" style={{ fontSize: 14, color: "#54525C" }}>
                Tap to upload or take a photo
              </span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          </>
        )}

        {image && (
          <div className="flex flex-col gap-3">
            <img
              src={image.preview}
              alt="Selected plant"
              className="w-full object-cover"
              style={{ maxHeight: 220, borderRadius: 12 }}
            />

            <div className="flex flex-col gap-1.5">
              <label className="font-body" style={{ fontSize: 13, color: "#54525C" }}>
                Anything you want to ask about it?
              </label>
              <input
                type="text"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="Optional — e.g. why are the leaves yellow?"
                className="w-full font-body rounded-xl px-3.5 py-3"
                style={{
                  border: "1px solid #C8C8F4",
                  backgroundColor: "#F8F8FF",
                  fontSize: 14,
                  color: "#1C1A3E",
                }}
              />
            </div>

            <button
              onClick={handleAnalyze}
              className="w-full font-body font-semibold text-white rounded-2xl py-3.5 transition-transform active:scale-[0.98]"
              style={{
                background: "linear-gradient(135deg, #5050C8, #1C1A3E)",
                fontSize: 15,
              }}
            >
              Take a look 🪴
            </button>
            <button
              onClick={() => { setImage(null); setError(null); }}
              className="font-body text-center underline"
              style={{ color: "#54525C", fontSize: 13 }}
            >
              Choose a different photo
            </button>
          </div>
        )}

        {error && (
          <div className="mt-4 p-4 rounded-2xl" style={{ backgroundColor: "#FFF0F0", border: "1px solid #FFD0D0" }}>
            <p className="font-body text-sm" style={{ color: "#CC3333" }}>{error}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlantSnapModal;
